.floor-care-page {
  --content-max: 1500px;
  --container-pad: clamp(16px, 4vw, 48px);
  --tap-min: 44px;
  --section-gap: clamp(32px, 6vw, 120px);

  .page {
    width: 100%;
    min-height: 100%;
    height: auto;
    overflow: visible;
    position: relative;
    background-color: rgba(255, 255, 255, 1);
  }

  // ---------------------------------------------------------------------------
  // Hero
  // ---------------------------------------------------------------------------
  .guide-hero {
    position: relative;
    width: 100%;
    min-height: clamp(560px, 72vh, 840px);
    background: url(../../../assets/images/autumn-harvest-collection.png) no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
  }

  .guide-hero__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba(22, 0, 4, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  .guide-hero__content {
    flex: 1;
    width: min(92vw, var(--content-max));
    padding: 0 var(--container-pad);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .guide-hero__title {
    margin: 0;
    color: rgba(255, 255, 255, 1);
    font-size: clamp(34px, 5vw, 64px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-transform: uppercase;
    line-height: 1.05;
  }

  .guide-hero__subtitle {
    margin: clamp(14px, 2.4vw, 24px) 0 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: clamp(16px, 1.6vw, 22px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.5;
  }

  // ---------------------------------------------------------------------------
  // Main
  // ---------------------------------------------------------------------------
  .guide-main {
    width: 100%;
  }

  // ---------------------------------------------------------------------------
  // Quick Care Cards
  // ---------------------------------------------------------------------------
  .care-cards {
    width: 100%;
    max-width: var(--content-max);
    margin: var(--section-gap) auto 0;
    padding-inline: var(--container-pad);
    box-sizing: border-box;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: clamp(18px, 2.2vw, 28px);
  }

  .care-card {
    padding: clamp(28px, 3vw, 40px) clamp(20px, 2.2vw, 28px);
    border: 1px solid rgba(22, 0, 4, 0.12);
    border-radius: 12px;
    text-align: center;
  }

  .care-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin: 0 0 20px;
    color: rgba(163, 135, 95, 1);

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .care-card__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(16px, 1.2vw, 19px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.3;
  }

  .care-card__text {
    margin: 10px 0 0;
    color: rgba(124, 124, 124, 1);
    font-size: 14px;
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.6;
  }

  .cleanerBadge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    padding: 10px 18px;

    border: 1px solid #d8c7a8;
    border-radius: 999px;

    color: #9c7a4d;
    background: #fbf8f2;

    font-weight: 600;
    text-decoration: none;

    transition: 0.2s;

    &:hover {
      background: #f4ecdd;
      transform: translateY(-1px);
    }
  }

  // ---------------------------------------------------------------------------
  // Article-style content sections (Daily / Deep Cleaning)
  // ---------------------------------------------------------------------------
  .article-section {
    width: 100%;
    max-width: 900px;
    margin: clamp(56px, 7vw, 96px) auto 0;
    padding-inline: var(--container-pad);
    padding-top: clamp(32px, 5vw, 56px);
    box-sizing: border-box;
    border-top: 1px solid rgba(22, 0, 4, 0.1);
  }

  .article-section__eyebrow {
    margin: 0 0 0.75rem;
    color: rgba(163, 135, 95, 1);
    font-size: 12px;
    font-family: Red Rose-Medium;
    font-weight: normal;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .article-section__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(24px, 2.4vw, 34px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.2;
  }

  .article-section__text {
    margin: clamp(12px, 1.6vw, 18px) 0 0;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 18px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.7;
  }

  .article-section__subtitle {
    margin: 2rem 0 0.75rem;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(18px, 1.5vw, 22px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.35;
  }

  .article-list {
    margin: 1rem 0 0;
    padding-left: 1.25rem;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 18px);
    font-family: Red Rose-Regular;

    li {
      margin-bottom: 0.65rem;
      line-height: 1.7;
    }
  }

  // ---------------------------------------------------------------------------
  // Things to Avoid
  // ---------------------------------------------------------------------------
  .avoid-section {
    width: 100%;
    max-width: var(--content-max);
    margin: clamp(56px, 7vw, 96px) auto 0;
    padding-inline: var(--container-pad);
    padding-top: clamp(32px, 5vw, 56px);
    box-sizing: border-box;
    border-top: 1px solid rgba(22, 0, 4, 0.1);
    text-align: center;

    .article-section__eyebrow,
    .article-section__title {
      text-align: center;
    }
  }

  .avoid-grid {
    margin: clamp(28px, 3.4vw, 44px) 0 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: clamp(16px, 2vw, 24px);
    text-align: left;
  }

  .avoid-card {
    padding: clamp(20px, 2.2vw, 28px);
    border: 1px solid rgba(22, 0, 4, 0.1);
    border-radius: 12px;
    background-color: rgba(22, 0, 4, 0.02);
  }

  .avoid-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: 0 0 14px;
    color: rgba(22, 0, 4, 0.55);

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .avoid-card__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: 16px;
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.3;
  }

  .avoid-card__text {
    margin: 8px 0 0;
    color: rgba(124, 124, 124, 1);
    font-size: 14px;
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.6;
  }

  // ---------------------------------------------------------------------------
  // Protect Your Investment
  // ---------------------------------------------------------------------------
  .protect-section {
    width: 100%;
    max-width: var(--content-max);
    margin: clamp(56px, 7vw, 96px) auto 0;
    padding-inline: var(--container-pad);
    padding-top: clamp(32px, 5vw, 56px);
    box-sizing: border-box;
    border-top: 1px solid rgba(22, 0, 4, 0.1);
    text-align: center;

    .article-section__eyebrow,
    .article-section__title {
      text-align: center;
    }
  }

  .protect-grid {
    margin: clamp(28px, 3.4vw, 44px) 0 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: clamp(16px, 2vw, 24px);
    text-align: left;
  }

  .protect-card {
    padding: clamp(20px, 2.2vw, 28px);
    border: 1px solid rgba(22, 0, 4, 0.1);
    border-radius: 12px;
  }

  .protect-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: 0 0 14px;
    color: rgba(163, 135, 95, 1);

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .protect-card__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: 16px;
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.3;
  }

  .protect-card__text {
    margin: 8px 0 0;
    color: rgba(124, 124, 124, 1);
    font-size: 14px;
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.6;
  }

  // ---------------------------------------------------------------------------
  // FAQ
  // ---------------------------------------------------------------------------
  .faq-section {
    width: 100%;
    max-width: 900px;
    margin: clamp(56px, 7vw, 96px) auto 0;
    padding-inline: var(--container-pad);
    padding-top: clamp(32px, 5vw, 56px);
    box-sizing: border-box;
    border-top: 1px solid rgba(22, 0, 4, 0.1);
  }

  .faq-list {
    margin: clamp(20px, 2.4vw, 28px) 0 0;
  }

  .faq-item {
    border-bottom: 1px solid rgba(22, 0, 4, 0.1);
  }

  .faq-item__heading {
    margin: 0;
    font-weight: normal;
  }

  .faq-item__trigger {
    width: 100%;
    padding: clamp(16px, 2vw, 22px) 0;
    background: none;
    border: none;
    appearance: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(16px, 1.2vw, 19px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    text-align: left;
    line-height: 1.4;
    min-height: var(--tap-min);

    &:focus-visible {
      outline: 2px solid rgba(22, 0, 4, 1);
      outline-offset: 3px;
    }
  }

  .faq-item__chevron {
    flex-shrink: 0;
    color: rgba(163, 135, 95, 1);
    transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .faq-item__panel {
    display: grid;
    // `minmax(0, 0fr)`, not a bare `0fr` — a plain fr track still respects the
    // grid item's automatic minimum size (which includes its padding), so it
    // wouldn't fully collapse; wrapping in minmax(0, …) overrides that.
    grid-template-rows: minmax(0, 0fr);
    transition: grid-template-rows 260ms cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    &.is-open {
      grid-template-rows: minmax(0, 1fr);
    }
  }

  .faq-item__answer {
    min-height: 0;
    overflow: hidden;
    margin: 0;
    padding-bottom: clamp(16px, 2vw, 22px);
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 17px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.7;
  }

  // ---------------------------------------------------------------------------
  // CTA
  // ---------------------------------------------------------------------------
  .cta-section {
    width: 100%;
    max-width: var(--content-max);
    margin: clamp(64px, 10vw, 160px) auto 0;
    padding: clamp(48px, 6vw, 96px) var(--container-pad);
    box-sizing: border-box;
    text-align: center;
    background-color: rgba(239, 236, 229, 1);
  }

  .cta-section__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(24px, 2.8vw, 38px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.25;
  }

  .cta-section__button {
    margin-top: clamp(24px, 3vw, 32px);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;
    padding: clamp(10px, 1.05vw, 14px) clamp(28px, 3vw, 40px);
    border-radius: 10px;
    border: 2px solid rgba(22, 0, 4, 1);
    background-color: rgba(22, 0, 4, 1);
    color: rgba(255, 255, 255, 1);
    font-size: clamp(15px, 1.05vw, 17px);
    font-family: Red Rose-Medium;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      background-color: transparent;
      color: rgba(22, 0, 4, 1);
    }

    &:active {
      transform: translateY(0);
    }
  }

  // ---------------------------------------------------------------------------
  // Breakpoints
  // ---------------------------------------------------------------------------
  @media (max-width: 1023px) {
    .guide-hero {
      min-height: clamp(460px, 62vh, 640px);
    }

    .care-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .avoid-grid,
    .protect-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 767px) {
    .guide-hero {
      min-height: clamp(360px, 54vh, 520px);
    }

    .care-cards {
      grid-template-columns: 1fr;
    }

    .avoid-grid,
    .protect-grid {
      grid-template-columns: 1fr;
    }

    .cta-section__button {
      width: 100%;
    }
  }
}
